import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, Chip, Divider, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { bloomberg } from '../theme/bloombergTheme';
import { DocPage, DocSection } from '../docs/docsTypes';
import { modelPortfoliosDoc } from '../docs/modelPortfolios';

const MONO = bloomberg.font.mono;

/**
 * Internal reference documentation.
 *
 * Deliberately not in navItems — reachable only by typing /docs. It documents
 * what each feature does, when to use it, and the actual formulas behind the
 * numbers, including where the implementation departs from the textbook
 * definition. Caveats are first-class rather than footnotes: a formula you
 * cannot trust is worth more clearly labelled than quietly omitted.
 */
const PAGES: DocPage[] = [modelPortfoliosDoc];

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: bloomberg.text.muted, mb: 0.75,
  }}>
    {children}
  </Typography>
);

const SectionBlock: React.FC<{ section: DocSection }> = ({ section }) => (
  <Box id={section.id} sx={{ mb: 5, scrollMarginTop: 64 }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: bloomberg.text.primary, mb: 0.5 }}>
      {section.title}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: bloomberg.text.secondary, mb: 2, maxWidth: 780 }}>
      {section.summary}
    </Typography>

    {section.useCase && (
      <Box sx={{
        borderLeft: `2px solid ${bloomberg.accent.orange}`, pl: 1.5, py: 0.5, mb: 2.5, maxWidth: 780,
      }}>
        <Label>When to use it</Label>
        <Typography sx={{ fontSize: 13, color: bloomberg.text.primary }}>{section.useCase}</Typography>
      </Box>
    )}

    {section.howTo && section.howTo.length > 0 && (
      <Box sx={{ mb: 2.5 }}>
        <Label>How to use it</Label>
        <Box component="ol" sx={{ m: 0, pl: 2.5, maxWidth: 780 }}>
          {section.howTo.map((step, i) => (
            <Typography key={i} component="li" sx={{ fontSize: 13, color: bloomberg.text.primary, mb: 0.5 }}>
              {step}
            </Typography>
          ))}
        </Box>
      </Box>
    )}

    {section.terms && section.terms.length > 0 && (
      <Box sx={{ mb: 2.5 }}>
        <Label>Terms</Label>
        {section.terms.map((t) => (
          <Box key={t.term} sx={{ mb: 1.25, maxWidth: 780 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: bloomberg.text.primary }}>
              {t.term}
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, color: bloomberg.text.secondary }}>
              {' — '}{t.definition}
            </Typography>
            {t.reading && (
              <Typography sx={{ fontSize: 12.5, color: bloomberg.text.muted, mt: 0.25, fontStyle: 'italic' }}>
                {t.reading}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    )}

    {section.formulas && section.formulas.length > 0 && (
      <Box sx={{ mb: 2.5 }}>
        <Label>Calculations</Label>
        {section.formulas.map((f) => (
          <Box key={f.name} sx={{
            mb: 1.5, maxWidth: 780, border: `1px solid ${bloomberg.border.primary}`,
            borderRadius: 1, overflow: 'hidden',
          }}>
            <Box sx={{ px: 1.5, py: 0.75, bgcolor: bloomberg.bg.tertiary }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: bloomberg.text.primary }}>
                {f.name}
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 1.25 }}>
              {/* Horizontally scrollable so a long expression never forces the
                  page itself to scroll sideways. */}
              <Box sx={{ overflowX: 'auto', mb: 1 }}>
                <Typography sx={{
                  fontFamily: MONO, fontSize: 12.5, color: bloomberg.accent.orange, whiteSpace: 'pre',
                }}>
                  {f.expression}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary }}>
                {f.reasoning}
              </Typography>
              {f.source && (
                <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: bloomberg.text.muted, mt: 0.75 }}>
                  {f.source}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )}

    {section.caveats && section.caveats.length > 0 && (
      <Box sx={{
        maxWidth: 780, border: `1px solid ${bloomberg.status.warning}`,
        bgcolor: bloomberg.status.warningBg, borderRadius: 1, px: 1.5, py: 1.25,
      }}>
        <Label>Caveats</Label>
        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
          {section.caveats.map((c, i) => (
            <Typography key={i} component="li" sx={{ fontSize: 12.5, color: bloomberg.text.primary, mb: 0.5 }}>
              {c}
            </Typography>
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

const Docs: React.FC = () => {
  const [page] = useState<DocPage>(PAGES[0]);
  const [query, setQuery] = useState('');

  // Plain substring match across every field — the content is small enough that
  // anything cleverer would be a liability rather than a feature.
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return page.sections;
    return page.sections.filter((s) =>
      JSON.stringify(s).toLowerCase().includes(q)
    );
  }, [page, query]);

  return (
    <Box sx={{ display: 'flex', gap: 4, p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Contents */}
      <Box sx={{
        width: 210, flexShrink: 0, position: 'sticky', top: 56, alignSelf: 'flex-start',
        display: { xs: 'none', md: 'block' }, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
      }}>
        <Label>Contents</Label>
        {page.sections.map((s) => (
          <Typography
            key={s.id}
            component="a"
            href={`#${s.id}`}
            sx={{
              display: 'block', fontSize: 12.5, color: bloomberg.text.secondary,
              textDecoration: 'none', py: 0.4,
              '&:hover': { color: bloomberg.accent.orange },
            }}
          >
            {s.title}
          </Typography>
        ))}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: bloomberg.text.primary }}>
              {page.title}
            </Typography>
            {page.route && (
              <Chip label={page.route} size="small" sx={{
                fontFamily: MONO, fontSize: 11, height: 20,
                bgcolor: bloomberg.bg.tertiary, color: bloomberg.text.secondary,
              }} />
            )}
            <Chip label="internal reference" size="small" sx={{
              fontSize: 10, height: 20, bgcolor: bloomberg.bg.tertiary, color: bloomberg.text.muted,
            }} />
          </Box>
          <Typography sx={{ fontSize: 13.5, color: bloomberg.text.secondary, mt: 1.5, maxWidth: 780 }}>
            {page.intro}
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Search terms, formulas, caveats…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ my: 3, width: 340, maxWidth: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: bloomberg.text.muted }} />
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={{ mb: 3, borderColor: bloomberg.border.primary }} />

        {sections.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: bloomberg.text.muted }}>
            Nothing matches “{query}”.
          </Typography>
        ) : (
          sections.map((s) => <SectionBlock key={s.id} section={s} />)
        )}
      </Box>
    </Box>
  );
};

export default Docs;
